import React, { useState, useEffect, useRef } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage, getQueuedScores } from "../../offlineQueue";
import * as XLSX from "xlsx";
import SwapCardsButton from "../../components/SwapCardsButton";
import { downloadBlob } from "../../nativeDownload";

const HCP_REVIEW_FORMATS = ["Singles Stableford", "Singles Medal", "Singles Stroke Play"];

// Mike's authoritative HCP allowance table (confirmed 2026-09-04): every
// format routed to this screen (Singles Stableford/Medal/Stroke Play, Skins,
// Team Stableford/Medal/Aggregate) uses a fixed 95% allowance EXCEPT Skins,
// which plays off full (100%) handicap — the event's old manual handicap_pct
// field is no longer used for the calculation on this screen. Better Ball,
// Foursomes, Greensomes and Match Play formats never reach this screen at
// all (see ScoreEntry.jsx routing) and keep their own separate allowances.
const HCP_95_FORMATS = ["Singles Stableford", "Singles Medal", "Singles Stroke Play", "Team Stableford", "Team Medal", "Team Aggregate Stableford", "Team Aggregate Stroke Play"];

export default function IndividualScoreEntry() {
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [holes, setHoles] = useState([]);
  const [scores, setScores] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [inputScores, setInputScores] = useState(Array(18).fill(""));
  const [currentHoleIdx, setCurrentHoleIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [toastColor, setToastColor] = useState("bg-green-600");
  const [error, setError] = useState("");
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [editPh, setEditPh] = useState({});
  const inputRefs = useRef([]);
  const scoresSaveTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const scoresRef = useRef({});
  const saveAllScoresRef = useRef(() => {});

  useEffect(() => { loadAll(); }, []);

  // Kept always-fresh so saveAllScores (called from a debounce timer and from
  // the unmount cleanup below) never reads a stale closure over `scores`.
  useEffect(() => { scoresRef.current = scores; }, [scores]);

  // The actual safety net: this fires on ANY change to the scores object,
  // regardless of what caused it — typing, Apply, Random Scores, per-player
  // Random. The trigger is the data changing, not a specific button, so
  // nothing typed or generated is ever lost just because Save/Apply was
  // never clicked before navigating away.
  useEffect(() => {
    if (!hasLoadedRef.current) { hasLoadedRef.current = true; return; } // skip the initial load-from-server population
    clearTimeout(scoresSaveTimerRef.current);
    scoresSaveTimerRef.current = setTimeout(() => saveAllScores(true), 600);
  }, [scores]);

  // Kept always-fresh (runs after every render, no deps) so the unmount
  // cleanup below never calls a stale saveAllScores closed over a render
  // where event/players hadn't loaded yet.
  useEffect(() => { saveAllScoresRef.current = saveAllScores; });

  // Safety net for navigating away before the debounce above has fired.
  useEffect(() => {
    return () => { clearTimeout(scoresSaveTimerRef.current); saveAllScoresRef.current(true); };
  }, []);

  const showToast = (msg, color = "bg-green-600") => {
    setToast(msg); setToastColor(color);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 5000);
  };

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setError("No active event."); setLoading(false); return; }
      setEvent(eventRes.data);
      const groupRes = await api.get("/groups");
      setPlayers((groupRes.data.players || []).filter(p => p.playing));
      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }
      const scoresRes = await api.get("/scores/" + eventRes.data.id);
      const map = {};
      scoresRes.data.forEach(s => {
        if (!map[s.player_id]) map[s.player_id] = {};
        map[s.player_id][s.hole] = s.gross;
      });
      // Overlay any score already typed and queued (but not yet applied
      // server-side, e.g. entered while offline) so navigating away and
      // back never looks like it was lost.
      const pending = getQueuedScores(eventRes.data.id);
      Object.entries(pending).forEach(([pid, holeScores]) => {
        map[pid] = { ...(map[pid] || {}), ...holeScores };
      });
      setScores(map);
    } catch(e) { setError("Failed to load: " + e.message); }
    finally { setLoading(false); }
  };

  const isLocked = (player) => !!player.verified_by_player_id;

  const savePhOverride = async (player, rawVal) => {
    const trimmed = String(rawVal).trim();
    const value = trimmed === "" ? null : parseInt(trimmed);
    if (value !== null && isNaN(value)) {
      setEditPh(prev => { const n = { ...prev }; delete n[player.player_id]; return n; });
      return;
    }
    if (event) {
      try {
        await api.put("/events/" + event.id + "/players/" + player.player_id + "/ph-override", { value });
        await loadAll();
      } catch(e) { showToast("Failed to save Play HCP override: " + e.message, "bg-red-600"); }
    }
    setEditPh(prev => { const n = { ...prev }; delete n[player.player_id]; return n; });
  };

  const handleRowClick = (player) => {
    if (isLocked(player)) {
      showToast("Score locked — verified by " + (player.verified_by_name || "marker"), "bg-red-600");
      return;
    }
    if (selectedPlayer) commitPlayerScores(selectedPlayer, inputScores);
    setSelectedPlayer(player);
    const existing = scores[player.player_id] || {};
    setInputScores(Array.from({length: 18}, (_, i) => existing[i+1] ? String(existing[i+1]) : ""));
    setCurrentHoleIdx(0);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      if (idx < 17 && inputRefs.current[idx+1]) inputRefs.current[idx+1].focus();
    }
  };

  // Stepper entry (replaces typing into 18 boxes): a hole with no score yet
  // starts pre-filled at that hole's own par -- most holes are played at or
  // near par, so this turns routine holes into a single Enter press instead
  // of typing a number every time. Up/down (buttons or arrow keys) nudge
  // from there; Enter commits the shown value and advances to the next hole.
  const stepperValue = () => {
    const existing = inputScores[currentHoleIdx];
    if (existing !== "" && existing !== undefined) return parseInt(existing);
    const par = holes[currentHoleIdx]?.par;
    return par || 4;
  };

  const setStepperValue = (val) => {
    const clamped = Math.max(1, Math.min(15, val));
    const next = [...inputScores];
    next[currentHoleIdx] = String(clamped);
    setInputScores(next);
    if (selectedPlayer && !isLocked(selectedPlayer)) commitPlayerScores(selectedPlayer, next);
  };

  const confirmHoleAndAdvance = () => {
    // Ensures a hole left untouched (still showing its par default) is
    // actually saved as that value, not silently skipped just because the
    // organiser never pressed +/-.
    setStepperValue(stepperValue());
    if (currentHoleIdx < 17) setCurrentHoleIdx(currentHoleIdx + 1);
  };

  const stepperKeyDown = (e) => {
    if (e.key === "ArrowUp") { e.preventDefault(); setStepperValue(stepperValue() + 1); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setStepperValue(stepperValue() - 1); }
    else if (e.key === "Enter") { e.preventDefault(); confirmHoleAndAdvance(); }
  };

  // Merges typed hole values into the scores object — local state only, no
  // network call. Persistence is entirely the job of the scores-watcher
  // effect above, which fires regardless of how scores got changed.
  const commitPlayerScores = (player, values) => {
    if (!player || isLocked(player)) return 0;
    const pid = player.player_id;
    let count = 0;
    const holeUpdates = {};
    values.forEach((val, idx) => {
      const g = parseInt(val);
      if (!isNaN(g) && g > 0) { holeUpdates[idx+1] = g; count++; }
    });
    if (count > 0) setScores(prev => ({...prev, [pid]: {...(prev[pid]||{}), ...holeUpdates}}));
    return count;
  };

  // Saves whatever is currently in `scores` — the one save path shared by
  // the auto-save watcher, Apply, and the unmount safety net.
  const saveAllScores = async (silent) => {
    if (!event) return 0;
    const allScores = [];
    Object.entries(scoresRef.current).forEach(([pid, holeScores]) => {
      const player = players.find(p => p.player_id === parseInt(pid));
      if (player && isLocked(player)) return;
      Object.entries(holeScores).forEach(([hole, gross]) => {
        if (gross) allScores.push({ player_id: parseInt(pid), hole: parseInt(hole), gross });
      });
    });
    if (!allScores.length) return 0;
    try {
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish: false });
      if (!silent) showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) {
      // Always surface a hard failure, even from the silent auto-save --
      // this is the safety net for anything typed between explicit saves,
      // so it must never fail invisibly. A queued (offline) save resolves
      // successfully above and never reaches here.
      showToast("Failed to save: " + e.message, "bg-red-600");
    }
    return allScores.length;
  };

  const handleApply = async () => {
    if (!selectedPlayer) { showToast("Please select a player first"); return; }
    if (isLocked(selectedPlayer)) {
      showToast("Cannot edit — score locked and verified", "bg-red-600");
      return;
    }
    const count = commitPlayerScores(selectedPlayer, inputScores);
    if (!count) { showToast("No scores entered for " + selectedPlayer.name); return; }
    clearTimeout(scoresSaveTimerRef.current);
    await saveAllScores(false);
  };

  const handleSave = async (publish) => {
    if (!event) return;
    try {
      const allScores = [];
      Object.entries(scores).forEach(([pid, holeScores]) => {
        const player = players.find(p => p.player_id === parseInt(pid));
        if (player && isLocked(player)) return;
        Object.entries(holeScores).forEach(([hole, gross]) => {
          allScores.push({ player_id: parseInt(pid), hole: parseInt(hole), gross });
        });
      });
      const res = await api.post("/scores/save", { event_id: event.id, scores: allScores, publish });
      showToast(queuedMessage(res, publish ? "Results published!" : "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message, "bg-red-600"); }
  };

  const handleClear = async () => {
    if (!event) return;
    const lockedPlayers = players.filter(p => isLocked(p));
    if (lockedPlayers.length > 0) {
      if (!window.confirm(lockedPlayers.length + " player(s) have verified locked scores which will be preserved. Clear only unlocked scores?")) return;
    } else {
      if (!window.confirm("Clear all scores?")) return;
    }
    try {
      const res = await api.post("/scores/clear", { event_id: event.id });
      const newScores = {};
      lockedPlayers.forEach(p => { if (scores[p.player_id]) newScores[p.player_id] = scores[p.player_id]; });
      setScores(newScores);
      setInputScores(Array(18).fill(""));
      setSelectedPlayer(null);
      showToast(queuedMessage(res, "Unlocked scores cleared"));
    } catch(e) { showToast("Failed: " + e.message, "bg-red-600"); }
  };

  const handleValidate = async () => {
    if (!event) return;
    setValidating(true);
    try {
      const res = await api.post("/scores/validate", { event_id: event.id });
      setValidationResult(res.data);
    } catch(e) {
      showToast("Validation failed: " + (e.response?.data?.error || e.message), "bg-red-600");
    } finally {
      setValidating(false);
    }
  };

  const handleRandom = () => {
    if (!holes.length) { showToast("No course loaded"); return; }
    const newScores = {...scores};
    players.forEach(p => {
      if (isLocked(p)) return;
      newScores[p.player_id] = {};
      holes.forEach(h => { newScores[p.player_id][h.hole] = h.par + Math.floor(Math.random() * 4); });
    });
    setScores({...newScores});
    showToast("Random scores generated");
  };

  const exportScores = () => {
    if (players.length === 0) { showToast("No players to export", "bg-red-600"); return; }
    const headers = ["Player", "WHS HCP", "Course HCP", ...holes.map(h => "H" + h.hole), "Gross Total"];
    const aoa = [headers];
    players.forEach(p => {
      const playerScores = scores[p.player_id] || {};
      const row = [p.name, p.handicap, p.hcp_adj ?? p.handicap];
      let total = 0;
      holes.forEach(h => {
        const g = playerScores[h.hole];
        row.push(g || "");
        if (g) total += parseInt(g);
      });
      row.push(total || "");
      aoa.push(row);
    });
    aoa.push([]);
    aoa.push(["Date:", event?.date ? new Date(event.date).toLocaleDateString('en-GB') : ""]);
    aoa.push(["Course:", event?.venue || ""]);
    aoa.push(["Format:", event?.game_type || ""]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Score Entry");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `ScoreEntry_${(event?.date || 'event').toString().split('T')[0]}.xlsx`);
  };

  const handleRandomPlayer = (e, player) => {
    e.stopPropagation();
    if (isLocked(player)) { showToast("Cannot edit locked score", "bg-red-600"); return; }
    const playerScores = {};
    holes.forEach(h => { playerScores[h.hole] = h.par + Math.floor(Math.random() * 5) - 1; });
    setScores(prev => ({...prev, [player.player_id]: playerScores}));
  };

  // Singles Match Play's handicap rule (confirmed by Mike 2026-09-04):
  // Playing HCP = player's own Course Handicap (hcp_adj) minus the LOWEST
  // Course Handicap in their own physical group (grp) — the lowest plays
  // off scratch (0), everyone else gets the difference. Scoped to grp, not
  // the whole event, since a bigger event can have several separate groups
  // each playing their own internal match.
  const singlesMatchPlayBaseline = (player) => {
    const groupmates = players.filter(p => p.grp === player.grp);
    return Math.min(...groupmates.map(p => (p.hcp_adj ?? p.handicap) || 0));
  };

  const getStats = (player) => {
    const isSinglesMatchPlay = event?.game_type === "Singles Match Play";
    const hcp_pct = HCP_95_FORMATS.includes(event?.game_type) ? 0.95 : 1;
    const playerHcpAdj = (player.hcp_adj ?? player.handicap) || 0;
    const calculatedPh = isSinglesMatchPlay
      ? Math.round(playerHcpAdj - singlesMatchPlayBaseline(player))
      : Math.round(playerHcpAdj * hcp_pct);
    const ph = (player.ph_override !== null && player.ph_override !== undefined) ? player.ph_override : calculatedPh;
    const base = Math.floor(ph / 18);
    const extra = ph % 18;
    const ps = scores[player.player_id] || {};
    let gross = 0, pts = 0, out_g = 0, in_g = 0;
    holes.forEach((h, idx) => {
      const g = ps[h.hole] || 0;
      if (g > 0) {
        const shots = base + (h.stroke_index <= extra ? 1 : 0);
        gross += g;
        pts += Math.max(0, 2 + (h.par - (g - shots)));
        if (idx < 9) out_g += g; else in_g += g;
      }
    });
    return { ph, playerHcpAdj, ps, gross, pts, out_g, in_g };
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");
  const isHcpFormat = HCP_REVIEW_FORMATS.includes(event?.game_type);
  const out_par = holes.slice(0,9).reduce((s,h) => s+h.par, 0);
  const in_par = holes.slice(9).reduce((s,h) => s+h.par, 0);
  const lockedCount = players.filter(p => isLocked(p)).length;

  return (
    <Layout>
      {toast && (
        <div style={{position:"fixed",top:"70px",left:"50%",transform:"translateX(-50%)",zIndex:9999,minWidth:"280px",textAlign:"center"}} className={toastColor + " text-white px-6 py-4 rounded-lg shadow-2xl text-base font-bold border-2 border-white"}>
          {toastColor === "bg-red-600" ? "⚠ " : "✓ "}{toast}
        </div>
      )}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">{["Team Stableford", "Team Medal"].includes(event?.game_type) ? "Team Aggregate Score Entry" : "Individual Score Entry"}</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type} | HCP: {HCP_95_FORMATS.includes(event.game_type) ? 95 : 100}%</span>}
          {!event?.course_id && <span className="text-xs text-red-500">No course linked</span>}
          {isHcpFormat && lockedCount > 0 && (
            <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
              Locked: {lockedCount}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => handleSave(true)} className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5 rounded">Publish Results</button>
            <button type="button" onClick={() => handleSave(false)} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={handleClear} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Clear All</button>
            <button type="button" onClick={handleValidate} disabled={validating} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold px-3 py-1.5 rounded" style={{opacity: validating ? 0.7 : 1}}>{validating ? "Checking..." : "✓ Validate Scores"}</button>
            <button type="button" onClick={handleRandom} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
            <button type="button" onClick={exportScores} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export to Excel</button>
            <SwapCardsButton players={players} eventId={event?.id} onSwapped={loadAll} />
          </div>
        </div>

        {holes.length > 0 && (
          <div className="bg-white border-b border-gray-300 overflow-x-auto">
            <table className="text-xs border-collapse" style={{minWidth:"900px"}}>
              <tbody>
                <tr className="bg-green-800 text-white">
                  <td className="px-2 py-1 font-bold w-16">Hole</td>
                  {holes.slice(0,9).map(h => <td key={h.hole} className="px-2 py-1 text-center w-8">{h.hole}</td>)}
                  <td className="px-2 py-1 text-center font-bold w-10">OUT</td>
                  {holes.slice(9).map(h => <td key={h.hole} className="px-2 py-1 text-center w-8">{h.hole}</td>)}
                  <td className="px-2 py-1 text-center font-bold w-10">IN</td>
                  <td className="px-2 py-1 text-center font-bold w-12">Total</td>
                </tr>
                <tr className="bg-gray-100">
                  <td className="px-2 py-1 font-bold">Par</td>
                  {holes.slice(0,9).map(h => <td key={h.hole} className="px-2 py-1 text-center">{h.par}</td>)}
                  <td className="px-2 py-1 text-center font-bold">{out_par}</td>
                  {holes.slice(9).map(h => <td key={h.hole} className="px-2 py-1 text-center">{h.par}</td>)}
                  <td className="px-2 py-1 text-center font-bold">{in_par}</td>
                  <td className="px-2 py-1 text-center font-bold">{out_par+in_par}</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-2 py-1 font-bold">SI</td>
                  {holes.slice(0,9).map(h => <td key={h.hole} className="px-2 py-1 text-center">{h.stroke_index}</td>)}
                  <td className="px-2 py-1 text-center">-</td>
                  {holes.slice(9).map(h => <td key={h.hole} className="px-2 py-1 text-center">{h.stroke_index}</td>)}
                  <td className="px-2 py-1 text-center">-</td>
                  <td className="px-2 py-1 text-center">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="bg-yellow-50 border-b border-gray-300 px-4 py-3">
          {!selectedPlayer && (
            <span className="text-xs text-gray-600">Click a player row to enter scores</span>
          )}
          {selectedPlayer && isLocked(selectedPlayer) && (
            <span className="text-xs text-red-600 font-semibold">LOCKED: {selectedPlayer.name}</span>
          )}
          {selectedPlayer && !isLocked(selectedPlayer) && (() => {
            const hole = holes[currentHoleIdx];
            const isFirst = currentHoleIdx === 0;
            const isLast = currentHoleIdx === 17;
            return (
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xs text-gray-600 font-semibold">{selectedPlayer.name}</span>
                <button type="button" onClick={() => currentHoleIdx > 0 && setCurrentHoleIdx(currentHoleIdx - 1)}
                  disabled={isFirst}
                  className="text-xs font-semibold px-2 py-1 rounded border border-gray-300 bg-white disabled:opacity-30">
                  ‹ Back
                </button>
                <div className="flex items-center gap-3 bg-white border border-gray-300 rounded px-3 py-1.5">
                  <span className="text-xs text-gray-500">Hole {hole?.hole}</span>
                  <span className="text-xs text-gray-400">Par {hole?.par} · SI {hole?.stroke_index}</span>
                  <button type="button" onClick={() => setStepperValue(stepperValue() - 1)}
                    className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 font-bold text-sm">−</button>
                  <input type="number" min="1" max="15" value={stepperValue()}
                    onChange={e => setStepperValue(parseInt(e.target.value) || 1)}
                    onKeyDown={stepperKeyDown}
                    ref={el => inputRefs.current[currentHoleIdx] = el}
                    className="w-12 h-9 text-center text-lg font-bold border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                  />
                  <button type="button" onClick={() => setStepperValue(stepperValue() + 1)}
                    className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 font-bold text-sm">+</button>
                </div>
                <button type="button" onClick={confirmHoleAndAdvance}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded">
                  {isLast ? "Confirm" : "Next ›"}
                </button>
                <div className="flex gap-0.5 ml-2">
                  {Array.from({length:18}, (_, i) => (
                    <button key={i} type="button" onClick={() => setCurrentHoleIdx(i)}
                      title={"Hole " + (i+1) + (inputScores[i] ? ": " + inputScores[i] : "")}
                      className={"w-4 h-4 rounded-sm text-[8px] leading-4 " +
                        (i === currentHoleIdx ? "bg-blue-600 text-white font-bold" :
                         inputScores[i] ? "bg-green-600 text-white" : "bg-gray-200 text-gray-500")}>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {error && <div className="bg-red-100 text-red-700 px-4 py-2 text-sm">{error}</div>}
        {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}

        <div className="flex-1 overflow-auto">
          <table className="text-xs border-collapse w-full" style={{minWidth:"1200px"}}>
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-3 py-2 text-left w-10">Grp</th>
                <th className="px-3 py-2 text-left w-36">Player</th>
                <th className="px-3 py-2 text-center w-16" title="Playing Handicap — Course HCP after this format's own allowance. Editable — click a value to manually override it for this event only.">
                  <div className="flex flex-col items-center gap-1">
                    <span>Play HCP</span>
                    <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded leading-tight">EDIT WITH CAUTION</span>
                  </div>
                </th>
                {Array.from({length:9},(_,i)=><th key={i} className="px-1 py-2 text-center w-8">{i+1}</th>)}
                <th className="px-2 py-2 text-center w-10 bg-green-900">OUT</th>
                {Array.from({length:9},(_,i)=><th key={i+9} className="px-1 py-2 text-center w-8">{i+10}</th>)}
                <th className="px-2 py-2 text-center w-10 bg-green-900">IN</th>
                <th className="px-2 py-2 text-center w-10 bg-green-900">GRS</th>
                <th className="px-2 py-2 text-center w-10 bg-green-900">{isMedal?"Net":"Pts"}</th>
                <th className="px-1 py-2 text-center w-8"></th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, i) => {
                const {ph, playerHcpAdj, ps, gross, pts, out_g, in_g} = getStats(player);
                const isSelected = selectedPlayer?.player_id === player.player_id;
                const locked = isLocked(player);
                let rowBg = i%2===0 ? "bg-white" : "bg-green-50";
                if (isSelected) rowBg = "bg-blue-100";
                if (locked) rowBg = "bg-red-50";
                return (
                  <tr key={player.id} onClick={() => handleRowClick(player)}
                    className={"cursor-pointer border-b border-gray-200 " + rowBg}>
                    <td className="px-3 py-1.5 font-bold text-green-700">{player.grp}</td>
                    <td className="px-3 py-1.5 font-medium">
                      {player.captain && <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-1 rounded mr-1">C</span>}
                      {player.name}
                      {locked && <span className="ml-2 text-xs text-red-600 font-semibold" title={"Verified by " + player.verified_by_name}>🔒</span>}
                      {isHcpFormat && player.hcp_review && !locked && <span className="ml-2 text-xs text-purple-600">📋</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                      {editPh[player.player_id] !== undefined ? (
                        <input type="number" value={editPh[player.player_id]}
                          onChange={e => setEditPh(prev => ({ ...prev, [player.player_id]: e.target.value }))}
                          onBlur={() => savePhOverride(player, editPh[player.player_id])}
                          onKeyDown={e => e.key === "Enter" && savePhOverride(player, editPh[player.player_id])}
                          placeholder="blank = auto"
                          className="border border-red-400 rounded px-1 py-0.5 w-14 text-xs text-center" autoFocus />
                      ) : (
                        <span className="cursor-pointer hover:text-red-700 inline-flex flex-col items-center"
                          title={player.ph_override != null ? "Manually overridden for this event — click to change, clear the box to restore the calculated value" : "Click to manually override this player's Play HCP for this event"}
                          onClick={() => setEditPh(prev => ({ ...prev, [player.player_id]: ph }))}>
                          {/* Course HCP is the step before the format's own %/Match Play
                              adjustment collapses it into Play HCP. Both numbers shown at
                              equal size when they differ -- a small grey caption under the
                              Play HCP wasn't visible enough (Mike missed it entirely on
                              2026-09-14), so this makes Course HCP just as prominent
                              instead of a secondary footnote. */}
                          {playerHcpAdj !== ph ? (
                            <div className="flex items-center gap-1">
                              <span className="flex flex-col items-center leading-none">
                                <span className="font-bold">{playerHcpAdj}</span>
                                <span className="text-[9px] text-gray-500 font-normal">Course</span>
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="flex flex-col items-center leading-none">
                                <span className="font-bold">{ph}</span>
                                <span className="text-[9px] text-gray-500 font-normal">Play</span>
                              </span>
                            </div>
                          ) : (
                            <span>{ph}</span>
                          )}
                          {player.ph_override != null && <span className="ml-1 text-red-600 font-bold" title="Manual override active">*</span>}
                        </span>
                      )}
                    </td>
                    {holes.slice(0,9).map(h => <td key={h.hole} className="px-1 py-1.5 text-center">{ps[h.hole]||""}</td>)}
                    <td className="px-2 py-1.5 text-center font-bold bg-gray-50">{out_g||""}</td>
                    {holes.slice(9).map(h => <td key={h.hole} className="px-1 py-1.5 text-center">{ps[h.hole]||""}</td>)}
                    <td className="px-2 py-1.5 text-center font-bold bg-gray-50">{in_g||""}</td>
                    <td className="px-2 py-1.5 text-center font-bold bg-gray-50">{gross||""}</td>
                    <td className="px-2 py-1.5 text-center font-bold text-blue-700 bg-gray-50">{gross ? (isMedal ? gross-ph : pts) : ""}</td>
                    <td className="px-1 py-1.5">
                      {!locked && (
                        <button type="button" onClick={(e) => handleRandomPlayer(e, player)}
                          style={{background:"#7c3aed",color:"white",border:"none",borderRadius:"4px",padding:"2px 6px",fontSize:"11px",cursor:"pointer",fontWeight:"700"}}>R</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && players.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500">No playing players. Go to Players screen first.</div>
          )}
        </div>
      </div>
      {validationResult && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "24px", maxWidth: "560px", width: "100%", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f766e", marginBottom: "8px" }}>✓ Score Validation</div>
            {validationResult.incomplete && validationResult.incomplete.length > 0 && (
              <div style={{ fontSize: "12px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", padding: "8px 12px", marginBottom: "12px" }}>
                Not all holes entered yet for: {validationResult.incomplete.join(", ")}. Checked what's there so far.
              </div>
            )}
            {validationResult.issues && validationResult.issues.length === 0 ? (
              <div style={{ fontSize: "14px", color: "#166534", padding: "12px 0" }}>No likely errors found across {validationResult.players_checked} player{validationResult.players_checked === 1 ? "" : "s"}.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
                {(validationResult.issues || []).map((issue, i) => (
                  <div key={i} style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px 14px" }}>
                    <div style={{ fontWeight: "700", fontSize: "13px", color: "#991b1b" }}>{issue.player} — Hole {issue.hole} (gross {issue.gross})</div>
                    <div style={{ fontSize: "12px", color: "#374151", marginTop: "2px" }}>{issue.reason}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "16px" }}>Advisory only — nothing has been changed. Double-check and correct any flagged scores yourself if needed.</div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setValidationResult(null)}
                style={{ background: "#0f766e", color: "white", border: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
